/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  MouseEventHandler,
  ReactNode,
  useCallback,
  useRef
} from 'react';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {
  BulkActionEntity,
  BulkActionType,
  CTCEntity,
  GenericObject
} from '@ion/core/src/types/entities';
import {CancelFromAllJobsForm} from '@ion/dls/src/editors/forms/CancelFromAllJobsForm';
import {MoveDetailsForm} from '@ion/dls/src/editors/forms/MoveDetailsForm';
import {ScheduleDeviceSyncForm} from '@ion/dls/src/editors/forms/ScheduleDeviceSyncForm';
import {UnpairForm} from '@ion/dls/src/editors/forms/UnpairForm';
import {
  JobDeviceFileTypeChooserPermissionsProps
} from '../components/jobs/JobTaskDeviceFileTypeChooser';
import {MessageForm} from './forms/MessageForm';
import AssetsBulkEditForm from './AssetsBulkEditForm';
import {CreateJobDetailsForm} from './CreateJobDetailsForm';
import DevicePasswordForm from './DevicePasswordForm';
import DevicesBulkEditForm from './DevicesBulkEditForm';
import GroupAssignmentDetailsForm from './GroupAssignmentDetailsForm';
import SCIUpdateDetailsForm from './SCIUpdateDetailsForm';
import ServiceEnablementForm from './ServiceEnablementForm';
import SwitchVehicleProfileDetailsForm from './SwitchVehicleProfileDetailsForm';
import {SCIUpdateProps} from './useSCIUpdate';
import VBusDetailsForm from './VBusStatusDetailsForm';

interface FormRef {
  onSubmit?: MouseEventHandler;
}

interface UseActionFormResult {
  form: ReactNode;
  onProcess: Function;
}

interface UseActionFormParams extends SCIUpdateProps,
  JobDeviceFileTypeChooserPermissionsProps {
  entity?: BulkActionEntity;
  selectedIdentifierType?: string;
  areTotalRowsSelected?: boolean;
  selectedIdentifiers?: string[];
  selectedRowEntities: CTCEntity[];
  actionType: BulkActionType;
  taskNickName: string;
  isSelectedFromGrid?: boolean;
  onBack: Function;
  setSelectedIdentifierType: (identifierType: string) => void;
  setTaskNickNameAction: (action: string) => void;
  setTaskNickName: (action: string) => void;
  onSave: (details: GenericObject) => void;
  isSelectedGridList: boolean;
  accountId?: number;
  userId?: number;
  serviceOptions?: string[];
  omitExtensions?: boolean;
  selectedEntitiesCount?: number;
  hasFile?: boolean;
  isAllDevicesSelected?: boolean;
  isIdentifiersPasted?: boolean;
  customFileUpload?: boolean;
  jobSearchEngineEnabled?: boolean;
  noConflictsDevices?: string[];
  setNoConflictsDevices?: Function;
  setIsCancelLoading?: Function;
  setIsProcessButtonDisabled?: Function;
}
/**
 * Hook that returns the corresponding form, details, and click event handler (for submitting the form) for the given
 * action type.
 */
export const useActionForm = ({
  entity,
  serviceOptions,
  accountId,
  actionType,
  userId,
  selectedRowEntities,
  selectedIdentifierType,
  areTotalRowsSelected,
  taskNickName,
  setTaskNickNameAction,
  onBack,
  onSave,
  setSelectedSCIDevices,
  selectedSCIDevices,
  setSelectedSCITypes,
  selectedSCITypes,
  setSelectedDeviceType,
  selectedDeviceType,
  setTaskNickName,
  setSelectedIdentifierType,
  Features,
  entitySelectionFormState,
  bulkImportParams,
  selectedEntitiesCount,
  selectedIdentifiers,
  setIsCancelLoading,
  noConflictsDevices,
  setNoConflictsDevices,
  setIsProcessButtonDisabled,
  hasFile = false,
  isSelectedFromGrid = false,
  customFileUpload = false,
  isIdentifiersPasted = false,
  isAllDevicesSelected = false,
  isSelectedGridList = false,
  omitExtensions = false,
  jobSearchEngineEnabled = false
}: UseActionFormParams): UseActionFormResult => {
  const formRef = useRef<FormRef>();

  // returns the form corresponding to the given action
  const getForm = (actionType: BulkActionType): ReactNode => {
    switch (actionType) {
      case BulkActionType.MOVE:
        return (
          <MoveDetailsForm ref={formRef}
            entity={entity}
            setTaskNickNameAction={setTaskNickNameAction}
            onSave={onSave} />
        );
      case BulkActionType.GROUP_ASSIGNMENT:
        return (
          <GroupAssignmentDetailsForm ref={formRef}
            identifierType={selectedIdentifierType}
            areTotalRowsSelected={areTotalRowsSelected}
            setTaskNickNameAction={setTaskNickNameAction}
            onSave={onSave}
            selectedRowEntities={selectedRowEntities}
            isSelectedGridList={isSelectedGridList}
            entitySelectionFormState={entitySelectionFormState}
            bulkImportParams={bulkImportParams}
            entity={entity}
            omitExtensions={omitExtensions} />
        );
      case BulkActionType.SERVICE_ENABLEMENT:
        return (
          <ServiceEnablementForm ref={formRef}
            services={serviceOptions}
            setTaskNickNameAction={setTaskNickNameAction}
            onSave={onSave} />
        );
      case BulkActionType.JOB_CREATION:
        return (
          <CreateJobDetailsForm ref={formRef}
            hasFile={hasFile}
            userId={userId}
            setIsCancelLoading={setIsCancelLoading}
            noConflictsDevices={noConflictsDevices}
            setNoConflictsDevices={setNoConflictsDevices}
            jobSearchEngineEnabled={jobSearchEngineEnabled}
            isSelectedFromGrid={isSelectedFromGrid}
            bulkImportParams={bulkImportParams}
            customFileUpload={customFileUpload}
            isIdentifiersPasted={isIdentifiersPasted}
            isAllDevicesSelected={isAllDevicesSelected}
            selectedIdentifierType={selectedIdentifierType}
            selectedIdentifiers={selectedIdentifiers}
            selectedEntitiesCount={selectedEntitiesCount}
            taskNickName={taskNickName}
            Features={Features}
            entitySelectionFormState={entitySelectionFormState}
            setTaskNickNameAction={setTaskNickNameAction}
            setIsProcessButtonDisabled={setIsProcessButtonDisabled}
            onSave={onSave} />
        );
      case BulkActionType.CANCEL_FROM_ALL_JOBS:
        return (
          <CancelFromAllJobsForm ref={formRef}
            onSave={onSave} />
        );
      case BulkActionType.SCHEDULE_DEVICE_SYNC:
        return (
          <ScheduleDeviceSyncForm ref={formRef}
            entity={entity}
            onSave={onSave} />
        );
      case BulkActionType.UNPAIR:
        return (
          <UnpairForm ref={formRef}
            entity={entity}
            onSave={onSave} />
        );
      case BulkActionType.SCI_UPDATE:
        return (
          <SCIUpdateDetailsForm ref={formRef}
            setTaskNickNameAction={setTaskNickNameAction}
            onSave={onSave}
            entity={entity}
            selectedRowEntities={selectedRowEntities}
            setSelectedSCIDevices={setSelectedSCIDevices}
            selectedSCIDevices={selectedSCIDevices}
            setSelectedSCITypes={setSelectedSCITypes}
            selectedSCITypes={selectedSCITypes}
            setSelectedDeviceType={setSelectedDeviceType}
            selectedDeviceType={selectedDeviceType}
            entitySelectionFormState={entitySelectionFormState}
            bulkImportParams={bulkImportParams} />
        );
      case BulkActionType.BULK_EDIT:
        if (isEqual(entity, BulkActionEntity.DEVICES)) {
          return (
            <DevicesBulkEditForm ref={formRef}
              isSelectedGridList={isSelectedGridList}
              onBack={onBack}
              accountId={accountId}
              identifierType={selectedIdentifierType}
              onSave={onSave} />
          );
        } else if (isEqual(entity, BulkActionEntity.ASSETS)) {
          return (
            <AssetsBulkEditForm ref={formRef}
              onSave={onSave} />
          );
        }
        return null;
      case BulkActionType.BULK_ADD:
        if (isEqual(entity, BulkActionEntity.DEVICES)) {
          return (
            <DevicesBulkEditForm isBulkAdd
              ref={formRef}
              accountId={accountId}
              taskNickName={taskNickName}
              setTaskNickName={setTaskNickName}
              setSelectedIdentifierType={setSelectedIdentifierType}
              identifierType={selectedIdentifierType}
              onSave={onSave} />
          );
        }
        return null;
      case BulkActionType.VBUS:
        return (
          <VBusDetailsForm ref={formRef}
            setTaskNickNameAction={setTaskNickNameAction}
            onSave={onSave} />
        );
      case BulkActionType.ASSIGN_REGION:
        return (
          <MessageForm ref={formRef}
            message={translate('common:editors.bulkActions.inventoryActivate')}
            onSave={onSave} />
        );
      case BulkActionType.VEHICLE_PROFILE:
        return (
          <SwitchVehicleProfileDetailsForm ref={formRef}
            onSave={onSave}/>
        );
      case BulkActionType.DEVICE_PASSWORD:
        if (isEqual(entity, BulkActionEntity.DEVICES)) {
          return (
            <DevicePasswordForm ref={formRef}
              onSave={onSave}/>
          );
        }
        return null;
      default:
        <h1>No Data</h1>;
        break;
    }
  };

  const onProcess = useCallback(function(e): void {
    formRef.current?.onSubmit?.(e);
  }, []);

  return {
    form : getForm(actionType),
    onProcess
  };
};
